import React, { useEffect, useState } from 'react';
import { Send, X, Megaphone, Loader2, Check, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BUDGETS = ['< $500', '$500 – $2k', '$2k – $5k', '$5k – $20k', '$20k+'];

const InquiryForm = ({ slotId, onClose }) => {
  const [form, setForm] = useState({
    name: '', email: '', company: '', budget: '', placement: slotId || '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10 || form.name.trim().length < 2 || !form.email.trim()) {
      setError('Fill in name, email, and at least a sentence about what you have in mind.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/ads/inquire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          budget: form.budget || null,
          placement: form.placement || null,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not submit. Try emailing ryan@outlawmedia.com.au directly.');
      }
      setDone(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="ad-inquiry-success">
        <Check className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
        <p className="text-lg font-black text-white">Inquiry received.</p>
        <p className="text-sm text-gray-400 mt-1">Ryan will get back to you within 48hrs.</p>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 inline-flex items-center justify-center px-5 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm transition-colors"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="ad-inquiry-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1">Name</label>
          <input
            type="text" required maxLength={120}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="ad-inquiry-name"
            className="w-full bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          />
        </div>
        <div>
          <label className="block text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1">Email</label>
          <input
            type="email" required maxLength={160}
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="ad-inquiry-email"
            className="w-full bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1">Company / Brand <span className="text-gray-600 normal-case font-normal">(optional)</span></label>
        <input
          type="text" maxLength={160}
          value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
          data-testid="ad-inquiry-company"
          className="w-full bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1">Monthly Budget</label>
          <select
            value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
            data-testid="ad-inquiry-budget"
            className="w-full bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          >
            <option value="">Select…</option>
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1">Placement</label>
          <input
            type="text" maxLength={80}
            value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })}
            placeholder="home-top, sidebar, video gallery…"
            data-testid="ad-inquiry-placement"
            className="w-full bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none placeholder-gray-600"
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-black tracking-widest uppercase text-gray-400 mb-1">What do you want to advertise?</label>
        <textarea
          required maxLength={4000} rows={4}
          value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Tell us about your product, target audience, and what success looks like."
          data-testid="ad-inquiry-message"
          className="w-full bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none resize-y placeholder-gray-600"
        />
      </div>
      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded bg-red-950/40 border border-red-500/50 text-red-200 text-xs" data-testid="ad-inquiry-error">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        data-testid="ad-inquiry-submit"
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-black text-sm uppercase tracking-widest transition-colors"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {submitting ? 'Sending…' : 'Send Inquiry'}
      </button>
      <p className="text-[10px] text-gray-600 text-center">
        Goes straight to ryan@outlawmedia.com.au - we reply within 48hrs.
      </p>
    </form>
  );
};

const AdSlot = ({ slotId, label = 'Advertisement', className = '', aspect = 'aspect-[4/1]' }) => {
  const [ad, setAd] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/ads/${encodeURIComponent(slotId)}`, { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!cancelled) { setAd(data); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [slotId]);

  // Lock body scroll while inquiry modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  return (
    <>
      <section
        className={`px-4 ${className}`}
        data-testid={`ad-slot-${slotId}`}
        aria-label={label}
      >
        <div className="max-w-3xl mx-auto">
          <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600 font-black mb-1">{label}</p>
          {ad ? (
            <a
              href={ad.click_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid={`ad-slot-${slotId}-link`}
              onClick={() => {
                // Fire-and-forget click event so advertiser reports track
                // real clickthroughs. Never blocks the navigation.
                if (ad.id) {
                  fetch(`${API}/api/ads/click`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ad_id: ad.id }),
                    keepalive: true,
                  }).catch(() => {});
                }
              }}
              className={`block ${aspect} rounded-xl overflow-hidden border border-zinc-800/80 hover:border-red-500/40 transition-colors`}
            >
              <img
                src={ad.image_url}
                alt={ad.alt || 'Sponsored'}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid={`ad-slot-${slotId}-empty`}
              aria-label="Advertise with Outlaw Media"
              className={`block w-full ${aspect} rounded-xl overflow-hidden border border-dashed border-zinc-700 hover:border-red-500/60 bg-zinc-950/60 hover:bg-zinc-900/80 group transition-all`}
            >
              <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-4 text-center">
                <Megaphone className="h-5 w-5 text-red-500/70 group-hover:text-red-400 transition-colors" />
                <p className="text-xs sm:text-sm font-black uppercase tracking-widest text-gray-400 group-hover:text-white">
                  Advertise with <span className="text-red-500">Outlaw Media</span>
                </p>
                <p className="text-[10px] text-gray-600 group-hover:text-gray-400">Tap to inquire</p>
              </div>
            </button>
          )}
          {!loaded && (
            <p className="text-[9px] text-gray-700 mt-0.5">loading…</p>
          )}
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-[55] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="ad-inquiry-backdrop"
        >
          <div
            className="relative w-full max-w-lg bg-zinc-950 border border-red-900/50 rounded-t-2xl sm:rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="ad-inquiry-modal"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute top-3 right-3 text-gray-400 hover:text-white z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-1">
                <Megaphone className="h-5 w-5 text-red-500" />
                <h2 className="text-xl sm:text-2xl font-black text-white">Advertise with Outlaw Media</h2>
              </div>
              <p className="text-xs sm:text-sm text-gray-400 mb-4">
                Reach Australia's most engaged crime-news audience. Tell us what you have in mind and Ryan will respond personally.
              </p>
              <InquiryForm slotId={slotId} onClose={() => setOpen(false)} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdSlot;
